/* eslint-disable no-unused-vars */
import React, { useMemo, useRef, RefObject } from 'react';
import _ from 'underscore';
import classnames from 'classnames';
import { Button, Glyphicon } from '@bingads-webui-react/primitive';
import { ErrorMessages } from '@bingads-webui-react/validation';
import { useFilePickerContext } from '../context';
import { i18nKeys } from '../constants';
import { IFilePickerData, OnFileChangeFn, ValidatorFn } from '../types';

type Props = {
  data: IFilePickerData;
  onFileChange: OnFileChangeFn;
  validator: ValidatorFn;
  allowedFileExtensions: string[];
  className?: string;
  onDelete?: (id?: IFilePickerData['id']) => void;
  customPlaceholder?: string;
  exploreBtnStyle?: string;
  required?: boolean;
  ariaLabel?: string;
};

export function FilePickerView({
  data,
  className,
  onDelete,
  onFileChange,
  allowedFileExtensions,
  validator,
  customPlaceholder,
  exploreBtnStyle,
  required,
  ariaLabel,
}: Props) {
  const { getString } = useFilePickerContext();
  const filePicker: RefObject<HTMLInputElement> = useRef(null);
  const {
    isValid = true,
    errorMessages = [],
  } = data || {};

  /* istanbul ignore next */
  const showFilePicker = () => filePicker.current!.click();

  const handleFileChange = ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
    const file = files![0];
    const { id } = data;
    onFileChange({
      id,
      file,
      ...validator(file),
    });
  };

  // TODO: use i18n.getString('FilePicker_Placeholder') as default value
  const placeholder = useMemo(() => (customPlaceholder || ''), [customPlaceholder]);
  return (
    <div className={classnames('file-picker-row', className, { 'file-picker-error': !isValid })} data-file-id={data.id}>
      <div className="picker-row">
        <input
          className="form-control file-picker-file-name"
          type="text"
          aria-label={ariaLabel}
          readOnly
          required={required}
          value={data.displayName || placeholder}
        />
        <Button className="file-picker-explore-btn" btnStyle={exploreBtnStyle} onClick={showFilePicker}>{getString(i18nKeys.FilePicker_ExploreFile)}</Button>

        {data.file !== null && (
          <Button
            btnStyle="link"
            className="file-picker-remove-btn"
            onClick={() => onDelete(data.id)}
            aria-label={getString(i18nKeys.FilePicker_RemoveFile)}
          >
            <Glyphicon className="file-picker-remove-btn-icon" glyph="ba-Cancel" />
            <span className="file-picker-remove-btn-text">{getString(i18nKeys.FilePicker_RemoveFile)}</span>
          </Button>
        )}

        <input
          id={`input_${data.id}`}
          type="file"
          onChange={handleFileChange}
          ref={filePicker}
          style={{ display: 'none' }}
          accept={allowedFileExtensions.join(',')}
          required={required}
        />
      </div>
      {!_.isEmpty(errorMessages) && (
        <div className="error-message-row">
          <ErrorMessages messages={errorMessages} />
        </div>
      )}
    </div>
  );
}

FilePickerView.defaultProps = {
  className: '',
  onDelete: _.noop,
  customPlaceholder: '',
  exploreBtnStyle: 'primary',
  required: false,
  ariaLabel: '',
};
